import { View } from "@-/com.npmjs.react-native";
import { Image } from "@-/com.npmjs.expo-image";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useCallback, useEffect, useRef } from "react";
import Animated, {
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  cancelAnimation,
} from "@-/com.npmjs.react-native-reanimated";
import HANI_STILL from "@-/dev.improve.haniverse.mascot/onboarding/perch-3d.png";
import manifest from "@-/dev.improve.haniverse.mascot/onboarding/perch-3d.json";
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";
const PerchImage = withUniwind(Image);

/** The 3D artwork's paw grip stays attached to the actual sheet's top edge. */
export function HaniSheetPerch({
  compact,
  onReady,
  progress,
  sheetTop,
  sheetWidth,
  size,
}: {
  compact: boolean;
  onReady: () => void;
  progress: SharedValue<number>;
  sheetTop: number;
  sheetWidth: number;
  size?: number;
}) {
  const width =
    size === undefined
      ? Math.min(sheetWidth, compact ? 104 : 176)
      : Math.min(sheetWidth, size);
  const scale = width / manifest.width;
  const revealStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: (1 - progress.get()) * manifest.height * scale }],
  }));
  return (
    <View
      accessibilityElementsHidden
      className="absolute z-10 overflow-hidden"
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      style={{
        height: manifest.pawBottom * scale,
        left: (sheetWidth - width) / 2,
        top: sheetTop - manifest.gripBaseline * scale,
        width,
      }}
      testID="onboarding-hani-sheet-perch"
    >
      <Animated.View style={[{ height: manifest.height * scale, width }, revealStyle]}>
        <PerchImage
          className="absolute inset-0"
          contentFit="contain"
          onError={onReady}
          onLoad={onReady}
          source={HANI_STILL}
          testID="onboarding-hani-still"
          transition={0}
        />
      </Animated.View>
    </View>
  );
}

/** A small, decorative speaker beside introductory copy, using the same artwork and lifecycle. */
export function HaniSpeechCompanion() {
  const size = 64;
  const progress = useSharedValue(0);
  const revealed = useRef(false);
  const releaseArtwork = useRef<() => void>(() => {});
  const onReady = useCallback(() => {
    if (!revealed.current) {
      revealed.current = true;
      progress.set(withTiming(1, { duration: 350 }));
    }
  }, [progress]);
  useEffect(() => {
    let active = true;
    void boundedPromise(
      { deadline: 1500, onDeadline: "resolve", operation: "Prepare Hani companion artwork" },
      (resolve) => {
        releaseArtwork.current = resolve;
      },
    ).then(() => {
      if (active) {
        onReady();
      }
    });
    return () => {
      active = false;
      releaseArtwork.current();
      cancelAnimation(progress);
    };
  }, [onReady, progress]);
  const handleReady = useCallback(() => releaseArtwork.current(), []);
  return (
    <View
      className="relative shrink-0"
      style={{ height: (manifest.height * size) / manifest.width, width: size }}
      testID="onboarding-hani-companion"
    >
      <HaniSheetPerch
        compact
        onReady={handleReady}
        progress={progress}
        sheetTop={(manifest.gripBaseline * size) / manifest.width}
        sheetWidth={size}
        size={size}
      />
    </View>
  );
}
